import type { AuthMeResponse } from "@/features/auth/model/auth-types";
import {
  UserBranchAccessStatus,
  WorkspaceAccessModule,
  WorkspaceAccessScope,
  WorkspaceMembershipRole,
} from "@/features/auth/model/auth-types";

export type WorkspaceAccessRecord = AuthMeResponse["workspaceAccess"][number];

const PARISH_ROUTE_SLUG = "parish";

function normalizeBranchSlug(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const t = value.trim().toLowerCase();
  return t.length > 0 ? t : null;
}

function slugMatches(left: string | null | undefined, right: string | null | undefined): boolean {
  const a = normalizeBranchSlug(left);
  const b = normalizeBranchSlug(right);
  return a !== null && b !== null && a === b;
}

export function getActiveWorkspaceAccesses(authMe: AuthMeResponse | null | undefined): WorkspaceAccessRecord[] {
  if (!authMe) return [];
  return authMe.workspaceAccess.filter((entry) => entry.status === "ACTIVE" && entry.workspace?.status === "ACTIVE");
}

export function getActiveUserBranchAccesses(
  access: WorkspaceAccessRecord | null | undefined,
): NonNullable<WorkspaceAccessRecord["userBranchAccesses"]> {
  if (!access) return [];
  const rows = access.userBranchAccesses ?? [];
  return rows.filter(
    (uba) => uba.status === UserBranchAccessStatus.ACTIVE && Boolean(uba.branch) && uba.branch?.status === "ACTIVE",
  );
}

export function getActiveNormalUserBranchAccesses(
  access: WorkspaceAccessRecord | null | undefined,
): NonNullable<WorkspaceAccessRecord["userBranchAccesses"]> {
  return getActiveUserBranchAccesses(access).filter((uba) => uba.branch?.branchType !== "SYSTEM");
}

/**
 * Workspace authority — true only for the workspace OWNER. Only the OWNER governs the workspace,
 * manages settings, expands scope, and grants parish access. Branch ADMINs are NOT workspace
 * authorities (their authority is per-branch, via {@link isBranchAdmin}).
 */
export function hasWorkspaceAuthorityAccess(access: WorkspaceAccessRecord | null | undefined): boolean {
  const hasAuthority = (() => {
    if (!access) return false;
    if (access.workspaceRole === WorkspaceMembershipRole.Owner) {
      return true;
    }
    return access.roles.some(
      (role) => role.roleScope === "WORKSPACE" && role.roleKey === WorkspaceMembershipRole.Owner,
    );
  })();

  return hasAuthority;
}

/**
 * Normal (non-SYSTEM) branches the user still holds an active {@link UserBranchAccess} to but whose branch
 * has been DEACTIVATED (`branch.status === "INACTIVE"`). These are intentionally excluded from
 * {@link getActiveUserBranchAccesses} (and therefore from the switcher), but we need them to tell a
 * "you navigated to a deactivated branch" case apart from "you never had access to this slug".
 */
export function getInactiveNormalUserBranchAccesses(
  access: WorkspaceAccessRecord | null | undefined,
): NonNullable<WorkspaceAccessRecord["userBranchAccesses"]> {
  if (!access) return [];
  const rows = access.userBranchAccesses ?? [];
  return rows.filter(
    (uba) =>
      uba.status === UserBranchAccessStatus.ACTIVE &&
      Boolean(uba.branch) &&
      uba.branch?.branchType !== "SYSTEM" &&
      uba.branch?.status === "INACTIVE",
  );
}

/** True when `branchSlug` refers to a normal branch the user has access to that is currently deactivated. */
export function isInactiveBranchSlug(
  access: WorkspaceAccessRecord | null | undefined,
  branchSlug: string | null | undefined,
): boolean {
  const normalized = normalizeBranchSlug(branchSlug);
  if (!normalized || normalized === PARISH_ROUTE_SLUG) return false;
  return getInactiveNormalUserBranchAccesses(access).some((uba) => slugMatches(uba.branch?.slug, normalized));
}

/**
 * True when the user has Organization (workspace) scope — the workspace OWNER, or any user holding a
 * WORKSPACE-scoped role. Used to decide whether a user blocked from a branch can fall back to the
 * organization surface rather than being dead-ended.
 */
export function hasOrganizationScopeAccess(access: WorkspaceAccessRecord | null | undefined): boolean {
  if (!access) return false;
  if (hasWorkspaceAuthorityAccess(access)) return true;
  return access.roles.some((role) => role.roleScope === "WORKSPACE");
}

export function resolveSystemBranchIdFromUserBranchAccess(
  access: WorkspaceAccessRecord | null | undefined,
): string | null {
  const system = getActiveUserBranchAccesses(access).find((uba) => uba.branch?.branchType === "SYSTEM");
  return system?.branch?.id ?? system?.branchId ?? null;
}

/** True when the active workspace is SINGLE_BRANCH ("mono") — mirrors {@link useShowMultiBranchWorkspaceUi}. */
export function isSingleBranchWorkspaceAccess(access: WorkspaceAccessRecord | null | undefined): boolean {
  return access?.workspace?.structureType === "SINGLE_BRANCH";
}

/** The workspace's single operational (NORMAL) branch id — the mono principal branch. */
export function resolvePrincipalNormalBranchId(
  access: WorkspaceAccessRecord | null | undefined,
): string | null {
  const normal = getActiveNormalUserBranchAccesses(access)[0];
  return normal?.branch?.id ?? normal?.branchId ?? null;
}

/**
 * What the `parish` URL token — a workspace URL with no branch segment — actually resolves to.
 *
 * ONE rule, and every layer must use it:
 *
 * - MULTI-branch: the parish/SYSTEM branch. `/:ws` is the organization console, which is a real place with its
 *   own books and its own nav.
 * - MONO: the workspace's single OPERATIONAL branch. A mono church has the same shape as a multi-branch one —
 *   parish plus one auto-created operational branch — but its parish is invisible: never navigated to, never
 *   granted, never operated on. Everything belongs to the operational branch, exactly as it would for a branch
 *   in a multi-branch workspace. The URL stays workspace-only; only the RESOLUTION differs.
 *
 * This existed four times over with three different answers (ability → operational, finance pages → parish,
 * form context → "whichever row sorts first", primary branch → "whatever carries the parish slug"), which is
 * how an owner came to configure finance on the parish while an invited user read the operational branch and
 * the two of them saw different books in the same church. The server now states the mono branch on `auth/me`
 * (`workspace.operationalBranch`); this resolves it from branch access for the same reason CASL rules are built
 * from access — the two must agree.
 */
export function resolveWorkspaceContextBranchAccess(
  access: WorkspaceAccessRecord | null | undefined,
): NonNullable<WorkspaceAccessRecord["userBranchAccesses"]>[number] | null {
  if (!access) return null;

  if (isSingleBranchWorkspaceAccess(access)) {
    const operational = getActiveNormalUserBranchAccesses(access)[0];
    if (operational) return operational;
    /*
     * No operational branch in reach. Falling back to the parish keeps a half-provisioned mono workspace
     * navigable instead of dead-ending, but it is a broken invariant: mono seeds exactly one.
     */
    return getActiveUserBranchAccesses(access).find((uba) => uba.branch?.branchType === "SYSTEM") ?? null;
  }

  return getActiveUserBranchAccesses(access).find((uba) => uba.branch?.branchType === "SYSTEM") ?? null;
}

/** The id of {@link resolveWorkspaceContextBranchAccess}'s branch. */
export function resolveWorkspaceContextBranchId(
  access: WorkspaceAccessRecord | null | undefined,
): string | null {
  if (isSingleBranchWorkspaceAccess(access)) {
    const operationalBranchId = access?.workspace?.operationalBranch?.id
    if (operationalBranchId) return operationalBranchId
  }
  const contextAccess = resolveWorkspaceContextBranchAccess(access);
  return contextAccess?.branch?.id ?? contextAccess?.branchId ?? null;
}

export function resolveBranchIdForWorkspaceUserAccess(
  access: WorkspaceAccessRecord | null | undefined,
  branchSlug: string | null | undefined,
): string | null {
  if (!access) return null;
  const normalized = normalizeBranchSlug(branchSlug);
  if (!normalized) return null;
  // The one rule, shared with every other layer — see `resolveWorkspaceContextBranchAccess`.
  if (normalized === PARISH_ROUTE_SLUG) return resolveWorkspaceContextBranchId(access);
  const match = getActiveUserBranchAccesses(access).find((uba) => slugMatches(uba.branch?.slug, normalized));
  if (match) return match.branch?.id ?? match.branchId;
  return access.departmentAccess?.find((department) => slugMatches(department.branchSlug, normalized))?.branchId ?? null;
}

/**
 * Default branch slug for routing from {@link UserBranchAccess} only (no `member.branch`).
 */
export function resolveBranchSlugFromUserBranchAccess(
  access: WorkspaceAccessRecord | null | undefined,
  preferredSlug: string | null | undefined,
): string | null {
  if (!access) return null;
  // Mono workspaces route branch-less: the bare `/:ws` (parish alias) IS the operational branch, so the
  // default landing collapses to the org URL — the principal branch is resolved server-side.
  if (isSingleBranchWorkspaceAccess(access)) {
    return PARISH_ROUTE_SLUG;
  }
  const all = getActiveUserBranchAccesses(access);
  const normals = getActiveNormalUserBranchAccesses(access);
  const departmentBranches = [...new Set((access.departmentAccess ?? []).map((department) => department.branchSlug))];
  const preferred = normalizeBranchSlug(preferredSlug);
  if (preferred) {
    const allowed = normals.some((u) => slugMatches(u.branch?.slug, preferred));
    return allowed || departmentBranches.some((slug) => slugMatches(slug, preferred)) ? preferred : null;
  }
  if (normals.length === 1) {
    return normals[0]?.branch?.slug ?? null;
  }
  if (normals.length === 0) {
    if (departmentBranches.length === 1) return departmentBranches[0] ?? null;
    const system = all.find((u) => u.branch?.branchType === "SYSTEM");
    return system ? PARISH_ROUTE_SLUG : null;
  }
  return null;
}

export function hasRolePermissionOnAnyNormalBranch(
  access: WorkspaceAccessRecord | null | undefined,
  module: WorkspaceAccessModule,
): boolean {
  if (!access) return false;
  const normalBranchIds = new Set(getActiveNormalUserBranchAccesses(access).map((uba) => uba.branchId));
  const prefix = `${module.toLowerCase()}.`;
  return access.roles.some(
    (role) =>
      role.roleStatus !== "INACTIVE" &&
      role.branchId !== null &&
      normalBranchIds.has(role.branchId) &&
      (role.permissions ?? []).some((permission) => permission.startsWith(prefix)),
  );
}

/**
 * True when the user governs the parish — i.e. has any active access to the SYSTEM (parish) branch.
 * Used to decide who may see the workspace-wide parish overview ("anyone who can manage anything in
 * the parish system branch"). Workspace OWNERs always qualify via {@link hasWorkspaceAuthorityAccess}.
 */
export function hasParishBranchAccess(access: WorkspaceAccessRecord | null | undefined): boolean {
  return getActiveUserBranchAccesses(access).some((uba) => uba.branch?.branchType === "SYSTEM");
}

export function resolveMembersModuleScopeForBranchSlug(
  access: WorkspaceAccessRecord | null | undefined,
  branchSlug: string | null | undefined,
): WorkspaceAccessScope {
  const normalized = normalizeBranchSlug(branchSlug);
  if (!access || !normalized) {
    return WorkspaceAccessScope.BRANCH;
  }
  const branchId = resolveBranchIdForWorkspaceUserAccess(access, normalized);
  const uba =
    branchId === null
      ? undefined
      : getActiveUserBranchAccesses(access).find((u) => u.branchId === branchId || u.branch?.id === branchId);
  if (uba?.branch?.branchType === "SYSTEM") {
    return WorkspaceAccessScope.WORKSPACE;
  }
  return WorkspaceAccessScope.BRANCH;
}
